import React, { useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "./ui/sheet";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "./ui/accordion";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import {
  Search,
  LayoutDashboard,
  FileSearch,
  PlusCircle,
  FolderOpen,
  ClipboardList,
  ClipboardCheck,
  Users,
  Shield,
  ShieldAlert,
  Settings,
  FileText,
  FileBarChart,
  ShieldCheck,
  ListChecks,
  FileBarChart2,
  BookCheck,
  GraduationCap,
  DollarSign,
  MapPin,
  HelpCircle,
  Mail,
  ChevronRight,
  BookOpen,
  Layers,
  Compass,
  X,
} from "lucide-react";

/* ── section category groups ── */
const GROUPS = [
  {
    id: "screening",
    labelKey: "help.groups.screening",
    sectionIds: ["dashboard", "screenings", "newScreening", "screeningDetail", "projects", "projectDetail"],
  },
  {
    id: "clients",
    labelKey: "help.groups.clients",
    sectionIds: ["documentTracking", "batchReview", "clientRegister"],
  },
  {
    id: "pep",
    labelKey: "help.groups.pep",
    sectionIds: [
      "pepRegister", "pepReviewQueue", "pepLifecycleSettings",
      "mlroDashboard", "strReports", "strDetail",
      "complianceReports", "auditReadiness", "complianceChecklist",
      "regulatoryReports", "policyAttestation",
    ],
  },
  {
    id: "audit",
    labelKey: "help.groups.audit",
    sectionIds: ["auditLogSearch", "trainingLog"],
  },
  {
    id: "admin",
    labelKey: "help.groups.admin",
    sectionIds: ["adminPanel", "billing"],
  },
  {
    id: "general",
    labelKey: "help.groups.general",
    sectionIds: ["gettingAround", "userRoles", "general", "privacyNotice"],
  },
];

const HelpCenter = ({ open, onOpenChange }) => {
  const { t } = useTranslation();
  const { isAdmin, isSuperAdmin } = useAuth();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");

  const helpSections = useMemo(() => {
    const sections = [
      {
        id: "dashboard",
        route: "/dashboard",
        icon: LayoutDashboard,
        titleKey: "help.dashboard.title",
        items: [
          { q: "help.dashboard.q1", a: "help.dashboard.a1" },
          { q: "help.dashboard.q2", a: "help.dashboard.a2" },
          { q: "help.dashboard.q3", a: "help.dashboard.a3" },
          { q: "help.dashboard.q4", a: "help.dashboard.a4" },
          { q: "help.dashboard.q5", a: "help.dashboard.a5" },
        ],
        roles: ["user", "admin", "super_admin"],
      },
      {
        id: "screenings",
        route: "/transactions",
        icon: FileSearch,
        titleKey: "help.screenings.title",
        items: [
          { q: "help.screenings.q1", a: "help.screenings.a1" },
          { q: "help.screenings.q2", a: "help.screenings.a2" },
          { q: "help.screenings.q3", a: "help.screenings.a3" },
          { q: "help.screenings.q4", a: "help.screenings.a4" },
          { q: "help.screenings.q5", a: "help.screenings.a5" },
          { q: "help.screenings.q6", a: "help.screenings.a6" },
          { q: "help.screenings.q7", a: "help.screenings.a7" },
          { q: "help.screenings.q8", a: "help.screenings.a8" },
          { q: "help.screenings.q9", a: "help.screenings.a9" },
        ],
        roles: ["user", "admin", "super_admin"],
      },
      {
        id: "newScreening",
        route: "/transactions/new",
        icon: PlusCircle,
        titleKey: "help.newScreening.title",
        items: [
          { q: "help.newScreening.q1", a: "help.newScreening.a1" },
          { q: "help.newScreening.q2", a: "help.newScreening.a2" },
          { q: "help.newScreening.q3", a: "help.newScreening.a3" },
          { q: "help.newScreening.q4", a: "help.newScreening.a4" },
          { q: "help.newScreening.q5", a: "help.newScreening.a5" },
          { q: "help.newScreening.q6", a: "help.newScreening.a6" },
          { q: "help.newScreening.q7", a: "help.newScreening.a7" },
          { q: "help.newScreening.q8", a: "help.newScreening.a8" },
          { q: "help.newScreening.q9", a: "help.newScreening.a9" },
          { q: "help.newScreening.q10", a: "help.newScreening.a10" },
          { q: "help.newScreening.q11", a: "help.newScreening.a11" },
          { q: "help.newScreening.q12", a: "help.newScreening.a12" },
          { q: "help.newScreening.q13", a: "help.newScreening.a13" },
        ],
        roles: ["user", "admin", "super_admin"],
      },
      {
        id: "screeningDetail",
        route: "/transactions/",
        matchPartial: true,
        icon: FileSearch,
        titleKey: "help.screeningDetail.title",
        items: [
          { q: "help.screeningDetail.q1", a: "help.screeningDetail.a1" },
          { q: "help.screeningDetail.q2", a: "help.screeningDetail.a2" },
          { q: "help.screeningDetail.q3", a: "help.screeningDetail.a3" },
          { q: "help.screeningDetail.q4", a: "help.screeningDetail.a4" },
          { q: "help.screeningDetail.q5", a: "help.screeningDetail.a5" },
          { q: "help.screeningDetail.q6", a: "help.screeningDetail.a6" },
          { q: "help.screeningDetail.q7", a: "help.screeningDetail.a7" },
          { q: "help.screeningDetail.q8", a: "help.screeningDetail.a8" },
          { q: "help.screeningDetail.q9", a: "help.screeningDetail.a9" },
          { q: "help.screeningDetail.q10", a: "help.screeningDetail.a10" },
          { q: "help.screeningDetail.q11", a: "help.screeningDetail.a11" },
          { q: "help.screeningDetail.q12", a: "help.screeningDetail.a12" },
          { q: "help.screeningDetail.q13", a: "help.screeningDetail.a13" },
          { q: "help.screeningDetail.q14", a: "help.screeningDetail.a14" },
          { q: "help.screeningDetail.q15", a: "help.screeningDetail.a15" },
          { q: "help.screeningDetail.q16", a: "help.screeningDetail.a16" },
          { q: "help.screeningDetail.q17", a: "help.screeningDetail.a17" },
          { q: "help.screeningDetail.q18", a: "help.screeningDetail.a18" },
          { q: "help.screeningDetail.q19", a: "help.screeningDetail.a19" },
          { q: "help.screeningDetail.q20", a: "help.screeningDetail.a20" },
          { q: "help.screeningDetail.q21", a: "help.screeningDetail.a21" },
          { q: "help.screeningDetail.q22", a: "help.screeningDetail.a22" },
          { q: "help.screeningDetail.q23", a: "help.screeningDetail.a23" },
          { q: "help.screeningDetail.q24", a: "help.screeningDetail.a24" },
          { q: "help.screeningDetail.q25", a: "help.screeningDetail.a25" },
          { q: "help.screeningDetail.q26", a: "help.screeningDetail.a26" },
          { q: "help.screeningDetail.q27", a: "help.screeningDetail.a27" },
          { q: "help.screeningDetail.q28", a: "help.screeningDetail.a28" },
          { q: "help.screeningDetail.q29", a: "help.screeningDetail.a29" },
          { q: "help.screeningDetail.q30", a: "help.screeningDetail.a30" },
          { q: "help.screeningDetail.q31", a: "help.screeningDetail.a31" },
          { q: "help.screeningDetail.q32", a: "help.screeningDetail.a32" },
          { q: "help.screeningDetail.q33", a: "help.screeningDetail.a33" },
          { q: "help.screeningDetail.q34", a: "help.screeningDetail.a34" },
          { q: "help.screeningDetail.q35", a: "help.screeningDetail.a35" },
        ],
        roles: ["user", "admin", "super_admin"],
      },
      {
        id: "projects",
        route: "/projects",
        icon: FolderOpen,
        titleKey: "help.projects.title",
        items: [
          { q: "help.projects.q1", a: "help.projects.a1" },
          { q: "help.projects.q2", a: "help.projects.a2" },
          { q: "help.projects.q3", a: "help.projects.a3" },
          { q: "help.projects.q4", a: "help.projects.a4" },
          { q: "help.projects.q5", a: "help.projects.a5" },
        ],
        roles: ["user", "admin", "super_admin"],
      },
      {
        id: "projectDetail",
        route: "/projects/",
        matchPartial: true,
        icon: FolderOpen,
        titleKey: "help.projectDetail.title",
        items: [
          { q: "help.projectDetail.q1", a: "help.projectDetail.a1" },
          { q: "help.projectDetail.q2", a: "help.projectDetail.a2" },
          { q: "help.projectDetail.q3", a: "help.projectDetail.a3" },
          { q: "help.projectDetail.q4", a: "help.projectDetail.a4" },
        ],
        roles: ["user", "admin", "super_admin"],
      },
      {
        id: "documentTracking",
        route: "/document-tracking",
        icon: ClipboardList,
        titleKey: "help.documentTracking.title",
        items: [
          { q: "help.documentTracking.q1", a: "help.documentTracking.a1" },
          { q: "help.documentTracking.q2", a: "help.documentTracking.a2" },
          { q: "help.documentTracking.q3", a: "help.documentTracking.a3" },
          { q: "help.documentTracking.q4", a: "help.documentTracking.a4" },
          { q: "help.documentTracking.q5", a: "help.documentTracking.a5" },
          { q: "help.documentTracking.q6", a: "help.documentTracking.a6" },
        ],
        roles: ["user", "admin", "super_admin"],
      },
      {
        id: "batchReview",
        route: "/batch-review",
        icon: ClipboardCheck,
        titleKey: "help.batchReview.title",
        items: [
          { q: "help.batchReview.q1", a: "help.batchReview.a1" },
          { q: "help.batchReview.q2", a: "help.batchReview.a2" },
          { q: "help.batchReview.q3", a: "help.batchReview.a3" },
          { q: "help.batchReview.q4", a: "help.batchReview.a4" },
        ],
        roles: ["user", "admin", "super_admin"],
      },
      {
        id: "clientRegister",
        route: "/client-register",
        icon: Users,
        titleKey: "help.clientRegister.title",
        items: [
          { q: "help.clientRegister.q1", a: "help.clientRegister.a1" },
          { q: "help.clientRegister.q2", a: "help.clientRegister.a2" },
          { q: "help.clientRegister.q3", a: "help.clientRegister.a3" },
          { q: "help.clientRegister.q4", a: "help.clientRegister.a4" },
        ],
        roles: ["user", "admin", "super_admin"],
      },
      {
        id: "pepRegister",
        route: "/pep-register",
        icon: Shield,
        titleKey: "help.pepRegister.title",
        items: [
          { q: "help.pepRegister.q1", a: "help.pepRegister.a1" },
          { q: "help.pepRegister.q2", a: "help.pepRegister.a2" },
          { q: "help.pepRegister.q3", a: "help.pepRegister.a3" },
          { q: "help.pepRegister.q4", a: "help.pepRegister.a4" },
          { q: "help.pepRegister.q5", a: "help.pepRegister.a5" },
        ],
        roles: ["admin", "super_admin"],
      },
      {
        id: "pepReviewQueue",
        route: "/pep-review",
        icon: ShieldAlert,
        titleKey: "help.pepReviewQueue.title",
        items: [
          { q: "help.pepReviewQueue.q1", a: "help.pepReviewQueue.a1" },
          { q: "help.pepReviewQueue.q2", a: "help.pepReviewQueue.a2" },
          { q: "help.pepReviewQueue.q3", a: "help.pepReviewQueue.a3" },
        ],
        roles: ["admin", "super_admin"],
      },
      {
        id: "pepLifecycleSettings",
        route: "/pep-lifecycle-settings",
        icon: Settings,
        titleKey: "help.pepLifecycleSettings.title",
        items: [
          { q: "help.pepLifecycleSettings.q1", a: "help.pepLifecycleSettings.a1" },
          { q: "help.pepLifecycleSettings.q2", a: "help.pepLifecycleSettings.a2" },
          { q: "help.pepLifecycleSettings.q3", a: "help.pepLifecycleSettings.a3" },
        ],
        roles: ["super_admin"],
      },
      {
        id: "mlroDashboard",
        route: "/mlro-dashboard",
        icon: Shield,
        titleKey: "help.mlroDashboard.title",
        items: [
          { q: "help.mlroDashboard.q1", a: "help.mlroDashboard.a1" },
          { q: "help.mlroDashboard.q2", a: "help.mlroDashboard.a2" },
          { q: "help.mlroDashboard.q3", a: "help.mlroDashboard.a3" },
        ],
        roles: ["admin", "super_admin"],
      },
      {
        id: "strReports",
        route: "/str-reports",
        icon: FileText,
        titleKey: "help.strReports.title",
        items: [
          { q: "help.strReports.q1", a: "help.strReports.a1" },
          { q: "help.strReports.q2", a: "help.strReports.a2" },
          { q: "help.strReports.q3", a: "help.strReports.a3" },
          { q: "help.strReports.q4", a: "help.strReports.a4" },
          { q: "help.strReports.q5", a: "help.strReports.a5" },
        ],
        roles: ["admin", "super_admin"],
      },
      {
        id: "strDetail",
        route: "/str-reports/",
        matchPartial: true,
        icon: FileText,
        titleKey: "help.strDetail.title",
        items: [
          { q: "help.strDetail.q1", a: "help.strDetail.a1" },
          { q: "help.strDetail.q2", a: "help.strDetail.a2" },
          { q: "help.strDetail.q3", a: "help.strDetail.a3" },
        ],
        roles: ["admin", "super_admin"],
      },
      {
        id: "complianceReports",
        route: "/report-settings",
        icon: FileBarChart,
        titleKey: "help.complianceReports.title",
        items: [
          { q: "help.complianceReports.q1", a: "help.complianceReports.a1" },
          { q: "help.complianceReports.q2", a: "help.complianceReports.a2" },
          { q: "help.complianceReports.q3", a: "help.complianceReports.a3" },
          { q: "help.complianceReports.q4", a: "help.complianceReports.a4" },
          { q: "help.complianceReports.q5", a: "help.complianceReports.a5" },
        ],
        roles: ["user", "admin", "super_admin"],
      },
      {
        id: "auditReadiness",
        route: "/audit-readiness",
        icon: ShieldCheck,
        titleKey: "help.auditReadiness.title",
        items: [
          { q: "help.auditReadiness.q1", a: "help.auditReadiness.a1" },
          { q: "help.auditReadiness.q2", a: "help.auditReadiness.a2" },
          { q: "help.auditReadiness.q3", a: "help.auditReadiness.a3" },
        ],
        roles: ["admin", "super_admin"],
      },
      {
        id: "complianceChecklist",
        route: "/compliance-checklist",
        icon: ListChecks,
        titleKey: "help.complianceChecklist.title",
        items: [
          { q: "help.complianceChecklist.q1", a: "help.complianceChecklist.a1" },
          { q: "help.complianceChecklist.q2", a: "help.complianceChecklist.a2" },
          { q: "help.complianceChecklist.q3", a: "help.complianceChecklist.a3" },
        ],
        roles: ["admin", "super_admin"],
      },
      {
        id: "regulatoryReports",
        route: "/regulatory-reports",
        icon: FileBarChart2,
        titleKey: "help.regulatoryReports.title",
        items: [
          { q: "help.regulatoryReports.q1", a: "help.regulatoryReports.a1" },
          { q: "help.regulatoryReports.q2", a: "help.regulatoryReports.a2" },
          { q: "help.regulatoryReports.q3", a: "help.regulatoryReports.a3" },
          { q: "help.regulatoryReports.q4", a: "help.regulatoryReports.a4" },
          { q: "help.regulatoryReports.q5", a: "help.regulatoryReports.a5" },
        ],
        roles: ["admin", "super_admin"],
      },
      {
        id: "policyAttestation",
        route: "/policy-attestation",
        icon: BookCheck,
        titleKey: "help.policyAttestation.title",
        items: [
          { q: "help.policyAttestation.q1", a: "help.policyAttestation.a1" },
          { q: "help.policyAttestation.q2", a: "help.policyAttestation.a2" },
          { q: "help.policyAttestation.q3", a: "help.policyAttestation.a3" },
        ],
        roles: ["admin", "super_admin"],
      },
      {
        id: "auditLogSearch",
        route: "/audit-log-search",
        icon: Search,
        titleKey: "help.auditLogSearch.title",
        items: [
          { q: "help.auditLogSearch.q1", a: "help.auditLogSearch.a1" },
          { q: "help.auditLogSearch.q2", a: "help.auditLogSearch.a2" },
          { q: "help.auditLogSearch.q3", a: "help.auditLogSearch.a3" },
          { q: "help.auditLogSearch.q4", a: "help.auditLogSearch.a4" },
          { q: "help.auditLogSearch.q5", a: "help.auditLogSearch.a5" },
          { q: "help.auditLogSearch.q6", a: "help.auditLogSearch.a6" },
          { q: "help.auditLogSearch.q7", a: "help.auditLogSearch.a7" },
        ],
        roles: ["admin", "super_admin"],
      },
      {
        id: "trainingLog",
        route: "/training-log",
        icon: GraduationCap,
        titleKey: "help.trainingLog.title",
        items: [
          { q: "help.trainingLog.q1", a: "help.trainingLog.a1" },
          { q: "help.trainingLog.q2", a: "help.trainingLog.a2" },
          { q: "help.trainingLog.q3", a: "help.trainingLog.a3" },
          { q: "help.trainingLog.q4", a: "help.trainingLog.a4" },
        ],
        roles: ["admin", "super_admin"],
      },
      {
        id: "adminPanel",
        route: "/admin",
        icon: Users,
        titleKey: "help.adminPanel.title",
        items: [
          { q: "help.adminPanel.q1", a: "help.adminPanel.a1" },
          { q: "help.adminPanel.q2", a: "help.adminPanel.a2" },
          { q: "help.adminPanel.q3", a: "help.adminPanel.a3" },
          { q: "help.adminPanel.q4", a: "help.adminPanel.a4" },
          { q: "help.adminPanel.q5", a: "help.adminPanel.a5" },
          { q: "help.adminPanel.q6", a: "help.adminPanel.a6" },
          { q: "help.adminPanel.q7", a: "help.adminPanel.a7" },
          { q: "help.adminPanel.q8", a: "help.adminPanel.a8" },
          { q: "help.adminPanel.q9", a: "help.adminPanel.a9" },
          { q: "help.adminPanel.q10", a: "help.adminPanel.a10" },
          { q: "help.adminPanel.q11", a: "help.adminPanel.a11" },
        ],
        roles: ["admin", "super_admin"],
      },
      {
        id: "billing",
        route: "/billing",
        icon: DollarSign,
        titleKey: "help.billing.title",
        items: [
          { q: "help.billing.q1", a: "help.billing.a1" },
          { q: "help.billing.q2", a: "help.billing.a2" },
          { q: "help.billing.q3", a: "help.billing.a3" },
          { q: "help.billing.q4", a: "help.billing.a4" },
          { q: "help.billing.q5", a: "help.billing.a5" },
          { q: "help.billing.q6", a: "help.billing.a6" },
          { q: "help.billing.q7", a: "help.billing.a7" },
          { q: "help.billing.q8", a: "help.billing.a8" },
        ],
        roles: ["super_admin"],
      },
      {
        // The menu, search, shortcuts and phone tab bar (lib/nav.js,
        // CommandPalette, useGlobalShortcuts, MobileTabBar, Sidebar).
        id: "gettingAround",
        route: null,
        icon: Compass,
        titleKey: "help.gettingAround.title",
        items: [
          { q: "help.gettingAround.q1", a: "help.gettingAround.a1" },
          { q: "help.gettingAround.q2", a: "help.gettingAround.a2" },
          { q: "help.gettingAround.q3", a: "help.gettingAround.a3" },
          { q: "help.gettingAround.q4", a: "help.gettingAround.a4" },
          { q: "help.gettingAround.q5", a: "help.gettingAround.a5" },
          { q: "help.gettingAround.q6", a: "help.gettingAround.a6" },
        ],
        roles: ["user", "admin", "super_admin"],
      },
      {
        id: "userRoles",
        route: null,
        icon: Users,
        titleKey: "help.userRoles.title",
        items: [
          { q: "help.userRoles.q1", a: "help.userRoles.a1" },
          { q: "help.userRoles.q2", a: "help.userRoles.a2" },
          { q: "help.userRoles.q3", a: "help.userRoles.a3" },
          { q: "help.userRoles.q4", a: "help.userRoles.a4" },
          { q: "help.userRoles.q5", a: "help.userRoles.a5" },
          { q: "help.userRoles.q6", a: "help.userRoles.a6" },
          { q: "help.userRoles.q7", a: "help.userRoles.a7" },
        ],
        roles: ["user", "admin", "super_admin"],
      },
      {
        id: "general",
        route: null,
        icon: MapPin,
        titleKey: "help.general.title",
        items: [
          { q: "help.general.q1", a: "help.general.a1" },
          { q: "help.general.q2", a: "help.general.a2" },
          { q: "help.general.q3", a: "help.general.a3" },
          { q: "help.general.q4", a: "help.general.a4" },
          { q: "help.general.q5", a: "help.general.a5" },
          { q: "help.general.q6", a: "help.general.a6" },
          { q: "help.general.q7", a: "help.general.a7" },
        ],
        roles: ["user", "admin", "super_admin"],
      },
      {
        // The public notice at /privacy (pages/Privacy.jsx). What it says is
        // pinned to the code by backend/tests/test_privacy_notice.py.
        id: "privacyNotice",
        route: null,
        icon: Shield,
        titleKey: "help.privacyNotice.title",
        items: [
          { q: "help.privacyNotice.q1", a: "help.privacyNotice.a1" },
          { q: "help.privacyNotice.q2", a: "help.privacyNotice.a2" },
          { q: "help.privacyNotice.q3", a: "help.privacyNotice.a3" },
        ],
        roles: ["user", "admin", "super_admin"],
      },
    ];

    const userRole = isSuperAdmin() ? "super_admin" : isAdmin() ? "admin" : "user";
    return sections.filter((s) => s.roles.includes(userRole));
  }, [isAdmin, isSuperAdmin]);

  const currentRouteSection = useMemo(() => {
    const path = location.pathname;
    const exact = helpSections.find((s) => s.route && s.route === path);
    if (exact) return exact.id;
    const partial = helpSections.find(
      (s) => s.route && s.matchPartial && path.startsWith(s.route) && path !== s.route
    );
    return partial ? partial.id : null;
  }, [location.pathname, helpSections]);

  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return helpSections;
    const q = searchQuery.toLowerCase();
    return helpSections
      .map((section) => {
        const matchingItems = section.items.filter(
          (item) =>
            t(item.q).toLowerCase().includes(q) ||
            t(item.a).toLowerCase().includes(q)
        );
        if (matchingItems.length > 0) return { ...section, items: matchingItems };
        if (t(section.titleKey).toLowerCase().includes(q)) return section;
        return null;
      })
      .filter(Boolean);
  }, [searchQuery, helpSections, t]);

  const defaultOpen = useMemo(() => {
    const ids = ["general"];
    if (currentRouteSection) ids.unshift(currentRouteSection);
    return ids;
  }, [currentRouteSection]);

  /* ── current page section object ── */
  const currentSection = helpSections.find((s) => s.id === currentRouteSection);

  /* ── group sections for rendering (skip when searching) ── */
  const groupedSections = useMemo(() => {
    if (searchQuery.trim()) return null;
    return GROUPS.map((g) => ({
      ...g,
      sections: filteredSections.filter((s) => g.sectionIds.includes(s.id)),
    })).filter((g) => g.sections.length > 0);
  }, [filteredSections, searchQuery]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[480px] p-0 flex flex-col overflow-hidden"
        style={{ background: "var(--c-bg-soft)" }}
      >
        {/* ── Header ── */}
        <SheetHeader
          className="px-5 pt-5 pb-4 flex-shrink-0"
          style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)" }}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background: "rgba(6,182,212,0.18)" }}>
                <BookOpen className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <SheetTitle className="text-white text-base font-semibold leading-tight">
                  {t("help.title")}
                </SheetTitle>
                <SheetDescription className="text-slate-400 dark:text-slate-500 text-xs mt-0.5">
                  {t("help.subtitle")}
                </SheetDescription>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("help.searchPlaceholder")}
              aria-label={t("help.searchPlaceholder")}
              data-testid="help-search"
              className="w-full pl-9 pr-11 py-2.5 sm:py-2 rounded-xl text-base sm:text-sm bg-slate-800/70 text-white border border-slate-700/60 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/60 focus:border-transparent transition-all"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                aria-label={t("help.clearSearch")}
                title={t("help.clearSearch")}
                data-testid="help-search-clear"
                className="absolute right-0 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-xl text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            )}
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1 min-h-0">
          <div className="px-4 py-4 space-y-4">

            {/* ── Current Page Hero ── */}
            {currentSection && !searchQuery && (
              <div
                className="rounded-xl p-4 border"
                style={{
                  background: "linear-gradient(135deg, #ecfeff 0%, #e0f2fe 100%)",
                  borderColor: "#a5f3fc",
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
                    style={{ background: "#06b6d4", color: "#fff" }}
                  >
                    <MapPin className="w-3 h-3" />
                    {t("help.currentPage")}
                  </span>
                  <span className="text-sm font-semibold text-cyan-900">
                    {t(currentSection.titleKey)}
                  </span>
                </div>
                <p className="text-xs text-cyan-800 leading-relaxed">
                  {t(`help.${currentSection.id}.tip`) !== `help.${currentSection.id}.tip`
                    ? t(`help.${currentSection.id}.tip`)
                    : t("help.subtitle")}
                </p>
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {([`help.${currentSection.id}.tip1`, `help.${currentSection.id}.tip2`, `help.${currentSection.id}.tip3`]).map((k, i) => {
                    const val = t(k);
                    if (val === k) return null;
                    return (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                        style={{ background: "rgba(6,182,212,0.12)", color: "#0e7490", border: "1px solid rgba(6,182,212,0.25)" }}
                      >
                        <ChevronRight className="w-3 h-3 flex-shrink-0" />
                        {val}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Search Results Count ── */}
            {searchQuery && (
              <div className="flex items-center gap-2 px-1">
                <Search className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
                <span className="text-xs text-slate-500 dark:text-slate-400" role="status" data-testid="help-results-count">
                  {filteredSections.length > 0
                    ? t("help.resultsCount", {
                        count: filteredSections.reduce((n, s) => n + s.items.length, 0),
                        sections: t("help.sectionsCount", { count: filteredSections.length }),
                      })
                    : t("help.noResults")}
                </span>
              </div>
            )}

            {/* ── No Results ── */}
            {filteredSections.length === 0 && (
              <div className="text-center py-12 px-4">
                <div className="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
                  <Search className="w-7 h-7 text-slate-300 dark:text-slate-600" />
                </div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">{t("help.noResults")}</p>
                <p className="text-xs text-slate-400 dark:text-slate-500">{t("help.noResultsHint")}</p>
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="mt-2 min-h-[44px] px-3 text-sm text-cyan-600 hover:text-cyan-700 dark:text-cyan-400 font-medium"
                >
                  {t("help.clearSearch")}
                </button>
              </div>
            )}

            {/* ── Grouped Accordion (normal mode) ── */}
            {!searchQuery && groupedSections && groupedSections.map((group) => (
              <div key={group.id}>
                {/* Group header */}
                <div className="flex items-center gap-2 px-1 mb-2">
                  <Layers className="w-3 h-3 text-slate-400 dark:text-slate-500" />
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                    {t(group.labelKey)}
                  </span>
                  <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                </div>

                <Accordion
                  type="multiple"
                  defaultValue={defaultOpen}
                  className="space-y-1.5"
                >
                  {group.sections.map((section) => {
                    const Icon = section.icon;
                    const isCurrent = section.id === currentRouteSection;
                    return (
                      <SectionItem
                        key={section.id}
                        section={section}
                        isCurrent={isCurrent}
                        Icon={Icon}
                        t={t}
                      />
                    );
                  })}
                </Accordion>
              </div>
            ))}

            {/* ── Flat Accordion (search mode) ── */}
            {searchQuery && filteredSections.length > 0 && (
              <Accordion
                type="multiple"
                defaultValue={filteredSections.map((s) => s.id)}
                key="search"
                className="space-y-1.5"
              >
                {filteredSections.map((section) => {
                  const Icon = section.icon;
                  const isCurrent = section.id === currentRouteSection;
                  return (
                    <SectionItem
                      key={section.id}
                      section={section}
                      isCurrent={isCurrent}
                      Icon={Icon}
                      t={t}
                      searchQuery={searchQuery}
                    />
                  );
                })}
              </Accordion>
            )}

          </div>
        </ScrollArea>

        {/* ── Footer ── */}
        <div className="flex-shrink-0 px-4 pt-3 border-t bg-white dark:bg-slate-900" style={{ borderColor: "var(--c-border)", paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
          <a
            href="mailto:support@audacia.mu?subject=Sherlock%20Transactions%20%E2%80%94%20Support%20request"
            className="flex items-center justify-center gap-2 w-full min-h-[44px] px-4 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90 active:scale-[0.98]"
            style={{ background: "linear-gradient(135deg, #171644 0%, #1e3a5f 100%)", color: "#fff" }}
          >
            <Mail className="w-4 h-4" />
            {t("help.contactSupport")}
          </a>
          <p className="text-[11px] text-slate-400 dark:text-slate-500 text-center mt-2">{t("help.footer")}</p>
        </div>
      </SheetContent>
    </Sheet>
  );
};

/* ── Reusable section item ── */
const SectionItem = ({ section, isCurrent, Icon, t, searchQuery }) => {
  const highlight = (text) => {
    if (!searchQuery) return text;
    const idx = text.toLowerCase().indexOf(searchQuery.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="bg-yellow-100 text-yellow-900 rounded px-0.5">{text.slice(idx, idx + searchQuery.length)}</mark>
        {text.slice(idx + searchQuery.length)}
      </>
    );
  };

  return (
    <AccordionItem
      value={section.id}
      className={`rounded-xl border overflow-hidden transition-all ${
        isCurrent && !searchQuery
          ? "border-cyan-200 dark:border-cyan-800 shadow-sm"
          : "border-slate-200 dark:border-slate-700"
      }`}
    >
      <AccordionTrigger className="px-3 py-3 min-h-[44px] hover:no-underline hover:bg-slate-50/80 dark:hover:bg-slate-800/60 transition-colors [&>svg]:text-slate-400">
        <div className="flex items-center gap-2.5 flex-1">
          <div
            className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
              isCurrent && !searchQuery
                ? "bg-cyan-100 text-cyan-600 dark:bg-cyan-900/50 dark:text-cyan-300"
                : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
          </div>
          <span
            className={`text-sm font-medium text-left ${
              isCurrent && !searchQuery ? "text-cyan-800 dark:text-cyan-200" : "text-slate-700 dark:text-slate-200"
            }`}
          >
            {t(section.titleKey)}
          </span>
          <Badge
            variant="secondary"
            className="ml-auto mr-2 text-[10px] px-1.5 py-0 h-4 font-medium bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-0"
          >
            {section.items.length}
          </Badge>
          {isCurrent && !searchQuery && (
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0 mr-1" />
          )}
        </div>
      </AccordionTrigger>

      <AccordionContent className="bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 px-3 pt-3 pb-2">
        <div className="space-y-0">
          {section.items.map((item, idx) => (
            <div
              key={idx}
              className="group py-2.5 border-b border-slate-50 dark:border-slate-800 last:border-0"
            >
              {/* Question row */}
              <div className="flex items-start gap-2.5 mb-1.5">
                <span
                  className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold"
                  style={{ background: "#e0f2fe", color: "#0369a1" }}
                >
                  {idx + 1}
                </span>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200 leading-snug flex-1">
                  {searchQuery ? highlight(t(item.q)) : t(item.q)}
                </p>
              </div>
              {/* Answer row */}
              <div className="ml-7 pl-2.5 border-l-2 border-slate-100 dark:border-slate-800">
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                  {searchQuery ? highlight(t(item.a)) : t(item.a)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

export default HelpCenter;
